"use client"

import styled from "@emotion/styled"
import Image from "next/image"
import React from "react"
import { Desktop, Mobile } from "ui"
// import Gallery from "./Gallery"
import HeroContent from "./HeroContent"
import HeroVideo from "./HeroVideo"

// Design assumptions:
// Desktop hero designed for 1920px width. We keep a fixed aspect ratio so the
// entire hero scales proportionally when the viewport shrinks.
// Choose an aspect ratio from provided mock (estimate ~1920x640 -> 3:1). Adjust if exact spec differs.
const HERO_ASPECT_RATIO = 1920 / 729 // 3:1 like ratio

const HeroShell = styled.section`
  width: 100%;
  position: relative;
  /* Provide top spacing similar to previous hero padding if needed */
`

const HeroAspectBox = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: ${HERO_ASPECT_RATIO};
  ${(props) => props.theme.breakpoints.down("md")} {
    aspect-ratio: auto;
  }
`

const HeroLayer = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: row-reverse; /* Flip order: content on right (RTL visual intent) */
  align-items: stretch;
  justify-content: stretch;
  overflow: visible;
  ${(props) => props.theme.breakpoints.down("md")} {
    flex-direction: column;
    position: relative;
  }
`

// Left (in RTL visual) / Content Block wrapper – using blue background, optional angled shape via pseudo element.
const ContentPanel = styled.div`
  z-index: 2;
  position: relative;
  flex: 0 0 45%;
  background: #1988e2;
  color: #fff;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 48px clamp(16px, 2.5vw, 56px);
  /* overflow: hidden; */
  direction: rtl;
  ${(props) => props.theme.breakpoints.down("md")} {
    flex: none;
    width: 100%;
    padding: 0px;
  }
  /* Angled divider on the right side (desktop) */
  &:after {
    content: "";
    position: absolute;
    bottom: 0;
    top: 0;
    right: calc(100% - 1px);
    width: 10%;
    background: inherit;
    /* Flipped triangle: point on top, full width at bottom */
    clip-path: polygon(0 0, 100% 100%, 0 100%);
    z-index: 10;
    ${(props) => props.theme.breakpoints.down("md")} {
      display: none;
    }
  }
`

const VisualPanel = styled.div`
  position: relative;
  flex: 1 1 auto;
  overflow: hidden;
  ${(props) => props.theme.breakpoints.down("md")} {
    width: 100%;
    flex: none;
    aspect-ratio: 360 / 234; /* Maintain a reasonable aspect ratio on mobile */
  }
`

// Dog below hero: scales with hero width, centered.
const DogWrapper = styled.div`
  width: 100%;
  margin: 0 auto 0; /* pull upwards slightly under hero */
  position: relative;
  z-index: 2;
  pointer-events: none;
  height: 140px;
  height: 6vw;
  ${(props) => props.theme.breakpoints.down("md")} {
    height: 30px;
    margin-bottom: 20px;
  }
`

const DogImage = styled(Image)`
  object-fit: contain;
  width: 100%;
  height: auto;
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-70%);
  width: 30%;
  height: auto;
  ${(props) => props.theme.breakpoints.down("md")} {
    transform: none;
    left: auto;
    right: 20px;
    width: 81px;
    height: 116px;
  }
`

const LogoWrapper = styled.div`
  position: absolute;
  /* Positions scale with viewport: min 12px, max design 50px (1920) */
  top: clamp(12px, 2.6vw, 50px);
  left: clamp(12px, 2.6vw, 50px);
  z-index: 3;
  /* Width scales: design 240px @1920 => 12.5vw, set min 120px for mobile */
  width: clamp(120px, 12.5vw, 240px);
  height: auto;
  pointer-events: none;
  ${(props) => props.theme.breakpoints.down("md")} {
    /* Slightly tighter positioning on very small viewports if needed */
    top: clamp(8px, 4vw, 32px);
    left: clamp(8px, 4vw, 32px);
  }
  img {
    /* Ensure Next Image scales with wrapper */
    width: 100% !important;
    height: auto !important;
  }
`

// Removed images array after replacing Gallery with video

const HeroBanner: React.FC = () => {
  return (
    <HeroShell>
      <HeroAspectBox>
        <HeroLayer>
          <ContentPanel>
            <HeroContent />
          </ContentPanel>
          <VisualPanel>
            <HeroVideo />
          </VisualPanel>
        </HeroLayer>
      </HeroAspectBox>
      <DogWrapper>
        <Desktop>
          <DogImage
            src="/landingpages/homeupgrades/main-dog-desktop.png"
            alt="Dog"
            width={600}
            height={600}
            priority
          />
        </Desktop>
        <Mobile>
          <DogImage
            src="/landingpages/homeupgrades/main-dog-mobile.png"
            alt="Dog"
            width={81}
            height={116}
            priority
          />
        </Mobile>
      </DogWrapper>
      <Desktop>
        <LogoWrapper>
          <Image
            src="/landingpages/homeupgrades/main-logo.svg"
            alt="אשטרום מגורים"
            width={240}
            height={138}
            priority
          />
        </LogoWrapper>
      </Desktop>
    </HeroShell>
  )
}

export default HeroBanner
